import apiClient from './api'
import { withListDefaults } from '@/lib/listFetch'
import type {
  ApiList,
  ApiResponse,
  CreateUserRequest,
  Documentable,
  ListQuery,
  PermissionMatrix,
  PortalUser,
  UpdateUserRequest,
  UserListQuery,
} from '@/types'

// Build JSON or multipart body. Laravel needs POST + `_method=PUT` for
// multipart updates (same pattern as service-item / document-library uploads).
function toUserPayload(
  body: CreateUserRequest | UpdateUserRequest
): CreateUserRequest | UpdateUserRequest | FormData {
  if (!body.avatar) {
    const { avatar: _avatar, ...rest } = body
    void _avatar
    return rest
  }

  const form = new FormData()
  Object.entries(body).forEach(([key, value]) => {
    if (key === 'avatar') return
    if (value === undefined || value === null) return
    if (typeof value === 'boolean') {
      form.append(key, value ? '1' : '0')
      return
    }
    form.append(key, String(value))
  })
  form.append('avatar', body.avatar)
  return form
}

function userConfig(body: CreateUserRequest | UpdateUserRequest) {
  return body.avatar
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : undefined
}

// Settings > User management (2.11.1-2.11.4 + 2.11.3). Role-gated Admin/Manager
// on the backend; delete is Admin-only at the service level. The task-assignee
// picker also consumes `list` (id / full_name / email only).
export const usersApi = {
  // 2.11.1 - paginated/filtered list. role/status are repeatable filters.
  list: (q: UserListQuery = {}) =>
    apiClient
      .get<ApiList<PortalUser>>('/users', { params: withListDefaults(q) })
      .then(r => r.data),

  // 2.11.2 - single user.
  get: (id: string) =>
    apiClient
      .get<ApiResponse<PortalUser>>(`/users/${id}`)
      .then(r => r.data.data as PortalUser),

  // 2.11.2 - create (multipart when avatar is attached).
  create: (body: CreateUserRequest) =>
    apiClient
      .post<ApiResponse<PortalUser>>(
        '/users',
        toUserPayload(body),
        userConfig(body)
      )
      .then(r => r.data.data as PortalUser),

  // 2.11.2 - update (POST + `_method=PUT` when avatar is attached).
  update: (id: string, body: UpdateUserRequest) => {
    if (body.avatar) {
      const form = toUserPayload(body) as FormData
      form.append('_method', 'PUT')
      return apiClient
        .post<ApiResponse<PortalUser>>(
          `/users/${id}`,
          form,
          userConfig(body)
        )
        .then(r => r.data.data as PortalUser)
    }
    return apiClient
      .put<ApiResponse<PortalUser>>(`/users/${id}`, toUserPayload(body))
      .then(r => r.data.data as PortalUser)
  },

  // 2.11.1/2.11.2 - (de)activate.
  setActive: (id: string, isActive: boolean) =>
    apiClient
      .patch<ApiResponse<PortalUser>>(`/users/${id}/status`, {
        is_active: isActive,
      })
      .then(r => r.data.data as PortalUser),

  // 2.11.4 - soft delete + GDPR anonymise (Admin only).
  remove: (id: string) =>
    apiClient.delete<ApiResponse<null>>(`/users/${id}`).then(r => r.data),

  // 2.11.3 - read module permission matrix.
  getPermissions: (id: string) =>
    apiClient
      .get<
        ApiResponse<{ permissions: PermissionMatrix }>
      >(`/users/${id}/permissions`)
      .then(r => r.data.data?.permissions as PermissionMatrix),

  // 2.11.3 - persist module permission matrix.
  updatePermissions: (id: string, permissions: PermissionMatrix) =>
    apiClient
      .put<
        ApiResponse<{ permissions: PermissionMatrix }>
      >(`/users/${id}/permissions`, { permissions })
      .then(r => r.data.data?.permissions as PermissionMatrix),

  // 2.11.2 — resend welcome / credential email (rotates temp password).
  resendWelcomeEmail: (id: string) =>
    apiClient
      .post<ApiResponse<PortalUser>>(`/users/${id}/resend-welcome-email`)
      .then(r => r.data),

  // 2.11.2 — document attachments.
  documents: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<Documentable>>(`/users/${id}/documents`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  uploadDocument: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return apiClient
      .post<ApiResponse<Documentable>>(`/users/${id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data.data as Documentable)
  },

  renameDocument: (id: string, documentId: string, name: string) =>
    apiClient
      .patch<
        ApiResponse<Documentable>
      >(`/users/${id}/documents/${documentId}/rename`, { name })
      .then(r => r.data.data as Documentable),

  deleteDocument: (id: string, documentId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/users/${id}/documents/${documentId}`)
      .then(r => r.data),
}

export default usersApi
